<script setup lang="ts">
import { Button, Input, ZhPanel, ZhSectionHeading, ZhSystemLabel } from '@zerohuman/ui'
import { computed, nextTick, onMounted, ref } from 'vue'
import { api, refusalCode, requestFailure, type SignInIdentity, type SignInWay } from '@/api'
import { codeRefused, linkResultMessage } from '@/lib/signInError'

/**
 * #636: the ways the signed-in person can sign in, and adding another — Google, Microsoft, GitHub or an email
 * address — to the same account. Whichever they use afterwards, they are the same one person holding the same
 * grants. A provider is added through its own sign-in, which comes back here saying what happened; an address is
 * added by typing back the code sent to it. Nothing here removes a way in or merges two people.
 *
 * The add-an-email code step (#522) has the sign-in screen's contract: "Send a new code" asks for another code for
 * the address the first one went to, without typing it again, and says when it went. The button doing the work says
 * so ("Sending...", "Adding...") while every other button is disabled. An error sits in the form it is about, under
 * its field, and the field points to it (`aria-describedby`); the field is marked `aria-invalid` only when what was
 * typed in it was refused: an address the API turned away, or a code. A failure to start adding a provider has no
 * field, so it stays a line at the foot of the panel.
 */
const NAMES: Record<SignInWay, string> = { google: 'Google', microsoft: 'Microsoft', github: 'GitHub', email: 'Email' }

const identities = ref<SignInIdentity[]>([])
const ways = ref<SignInWay[]>([])
const loading = ref(true)
const loadFailed = ref(false)
/**
 * The request in flight, so the button doing it can say so. One at a time: the lock covers the provider buttons
 * too. A provider left at `provider` once its sign-in starts: the page is going there.
 */
const pending = ref<'provider' | 'send' | 'resend' | 'verify' | null>(null)
const busy = computed(() => pending.value !== null)
/** A failure to start adding a provider: no field goes with it, so it is said at the foot of the panel. */
const panelError = ref<string | null>(null)
/**
 * The error of the step on screen, said under its field. `refused` is true only when the API turned away what was
 * typed in that field, which is what marks it `aria-invalid`.
 */
const fieldError = ref<{ message: string; refused: boolean } | null>(null)
/** A status line in the code step: a new code went. */
const notice = ref<string | null>(null)
const result = ref<{ ok: boolean; message: string } | null>(null)
const step = ref<'idle' | 'email' | 'code'>('idle')
const email = ref('')
/** The address the code step's codes go to: what was typed, trimmed, once a code was sent to it. */
const sentTo = ref('')
const code = ref('')

/** The ids an error line takes in each step, so that step's field can point to it. */
const ERROR_IDS = { email: 'account-add-email-error', code: 'account-add-code-error' } as const
/** The status and code the API answers too many codes for one address with (#2121): a wait, not a wrong address. */
const THROTTLE_STATUS = 429
const THROTTLE_REFUSAL = 'code_throttled'

/**
 * Whether a failed send turned away the address itself: an HTTP 4xx other than the throttle wait. A throttle, a
 * server fault (5xx, including a code the mail service would not send) or a request that never reached the API says
 * nothing about what was typed, so it does not mark the field invalid.
 */
function addressRefused(caught: unknown): boolean {
    const failure = requestFailure(caught)
    if (!failure || failure.status < 400 || failure.status >= 500) return false
    return failure.status !== THROTTLE_STATUS && refusalCode(caught) !== THROTTLE_REFUSAL
}

/** Clears every error and the status line, as every new action and leaving the code step do. */
function clearFeedback(): void {
    panelError.value = null
    fieldError.value = null
    notice.value = null
}

const providerWays = computed(() => ways.value.filter((way): way is Exclude<SignInWay, 'email'> => way !== 'email'))
const offersEmail = computed(() => ways.value.includes('email'))

async function load(): Promise<void> {
    try {
        const answer = await api.identities()
        // An answer without both lists is not one the panel can show: it reads as a failed load, never a broken page.
        if (!Array.isArray(answer.identities) || !Array.isArray(answer.ways)) {
            throw new Error('identities_unreadable')
        }
        identities.value = answer.identities
        ways.value = answer.ways
        loadFailed.value = false
    } catch {
        loadFailed.value = true
    } finally {
        loading.value = false
    }
}

onMounted(async () => {
    result.value = linkResultMessage(new URLSearchParams(window.location.search))
    await load()
})

async function addProvider(provider: Exclude<SignInWay, 'email'>): Promise<void> {
    if (busy.value) return
    pending.value = 'provider'
    clearFeedback()
    try {
        const { url } = await api.linkProvider(provider)
        window.location.assign(url)
    } catch (caught) {
        panelError.value = caught instanceof Error ? caught.message : `Could not start adding ${NAMES[provider]}.`
        pending.value = null
    }
}

/** "Email me a code": the first code for the typed address, after which the step asks for it. */
async function sendCode(): Promise<void> {
    if (busy.value) return
    if (await requestCode(email.value.trim(), 'send')) {
        step.value = 'code'
    }
}

/** "Send a new code" (#522): another code to the address the step is for, without typing it again. */
async function resendCode(): Promise<void> {
    if (busy.value) return
    if (await requestCode(sentTo.value, 'resend')) {
        notice.value = `We sent a new code to ${sentTo.value}.`
    }
}

/**
 * The send path both steps share: asks for a code for the address, with `action`'s button saying so meanwhile. On a
 * refusal the API's own words are the error and the step stays as it was, the typed code with it. True when a code
 * went. Only the first send can mark its field invalid: a refused resend says nothing about what is in the code box.
 */
async function requestCode(address: string, action: 'send' | 'resend'): Promise<boolean> {
    pending.value = action
    clearFeedback()
    await nextTick()
    try {
        await api.requestLinkCode(address)
        sentTo.value = address
        code.value = ''
        return true
    } catch (caught) {
        fieldError.value = {
            message: caught instanceof Error ? caught.message : 'We could not send a code. Try again.',
            refused: action === 'send' && addressRefused(caught),
        }
        return false
    } finally {
        pending.value = null
    }
}

async function confirmCode(): Promise<void> {
    if (busy.value) return
    pending.value = 'verify'
    clearFeedback()
    await nextTick()
    try {
        await api.linkEmail(sentTo.value, code.value.trim())
        result.value = { ok: true, message: `${sentTo.value} added. You can sign in with a code sent to it.` }
        step.value = 'idle'
        email.value = ''
        code.value = ''
        await load()
    } catch (caught) {
        // The API's words, or the fallback, and nothing added: "Send a new code" sits right under the field as the
        // next step, so the error itself gives no advice to ask for one.
        fieldError.value = {
            message: caught instanceof Error ? caught.message : 'That code did not work.',
            refused: codeRefused(caught),
        }
    } finally {
        pending.value = null
    }
}

function cancelEmail(): void {
    step.value = 'idle'
    code.value = ''
    clearFeedback()
}
</script>

<template>
	<ZhPanel measure="prose" class="space-y-3" aria-labelledby="account-sign-in-heading" data-testid="account-sign-in">
		<ZhSectionHeading id="account-sign-in-heading">How you sign in</ZhSectionHeading>
		<p
			v-if="result"
			:class="result.ok ? 'text-sm' : 'text-destructive text-sm'"
			role="status"
			data-testid="account-link-result"
		>
			{{ result.message }}
		</p>
		<p v-if="loading" class="text-muted-foreground text-sm">Loading…</p>
		<p v-else-if="loadFailed" class="text-muted-foreground text-sm" data-testid="account-sign-in-error">
			Could not load how you sign in. Reload the page to try again.
		</p>
		<template v-else>
			<ul class="space-y-1 text-sm" data-testid="account-identities">
				<li v-for="(identity, index) in identities" :key="index" class="flex items-center gap-2">
					<ZhSystemLabel as="span">{{ NAMES[identity.provider] }}</ZhSystemLabel>
					<span v-if="identity.email" class="text-muted-foreground">{{ identity.email }}</span>
				</li>
			</ul>
			<p class="text-muted-foreground text-sm">
				Add another way in and it signs you into this same account, with everything you hold.
			</p>
			<div class="flex flex-wrap gap-2">
				<Button
					v-for="way in providerWays"
					:key="way"
					type="button"
					size="sm"
					variant="outline"
					:disabled="busy"
					:data-testid="`account-add-${way}`"
					@click="addProvider(way)"
				>
					Add {{ NAMES[way] }}
				</Button>
				<Button
					v-if="offersEmail && step === 'idle'"
					type="button"
					size="sm"
					variant="outline"
					:disabled="busy"
					data-testid="account-add-email"
					@click="step = 'email'"
				>
					Add an email address
				</Button>
			</div>
			<form v-if="step === 'email'" class="space-y-2" data-testid="account-email-form" @submit.prevent="sendCode">
				<label for="account-add-email" class="block text-sm">Email address</label>
				<Input
					id="account-add-email"
					v-model="email"
					type="email"
					autocomplete="email"
					required
					:aria-invalid="fieldError?.refused ? 'true' : undefined"
					:aria-describedby="fieldError ? ERROR_IDS.email : undefined"
				/>
				<p v-if="fieldError" :id="ERROR_IDS.email" class="text-destructive text-sm" role="alert">
					{{ fieldError.message }}
				</p>
				<div class="flex gap-2">
					<Button type="submit" size="sm" :disabled="busy">
						{{ pending === 'send' ? 'Sending...' : 'Email me a code' }}
					</Button>
					<Button type="button" size="sm" variant="outline" :disabled="busy" @click="cancelEmail">Cancel</Button>
				</div>
			</form>
			<form v-else-if="step === 'code'" class="space-y-2" data-testid="account-code-form" @submit.prevent="confirmCode">
				<label for="account-add-code" class="block text-sm">
					We sent a six-digit code to {{ sentTo }}. Type it here to add the address.
				</label>
				<Input
					id="account-add-code"
					v-model="code"
					inputmode="numeric"
					autocomplete="one-time-code"
					maxlength="6"
					required
					:aria-invalid="fieldError?.refused ? 'true' : undefined"
					:aria-describedby="fieldError ? ERROR_IDS.code : undefined"
				/>
				<p v-if="fieldError" :id="ERROR_IDS.code" class="text-destructive text-sm" role="alert">
					{{ fieldError.message }}
				</p>
				<p v-if="notice" class="text-muted-foreground text-sm" role="status">{{ notice }}</p>
				<div class="flex flex-wrap gap-2">
					<Button type="submit" size="sm" :disabled="busy">
						{{ pending === 'verify' ? 'Adding...' : 'Add the address' }}
					</Button>
					<Button type="button" size="sm" variant="outline" :disabled="busy" @click="resendCode">
						{{ pending === 'resend' ? 'Sending...' : 'Send a new code' }}
					</Button>
					<Button type="button" size="sm" variant="outline" :disabled="busy" @click="cancelEmail">Cancel</Button>
				</div>
			</form>
			<p v-if="panelError" class="text-destructive text-sm" role="alert">{{ panelError }}</p>
		</template>
	</ZhPanel>
</template>
